import React, { useEffect, useState } from 'react';
import { Apple, Loader2, CheckCircle2, XCircle, Music } from 'lucide-react';
import { toast } from 'sonner';
import {
  getMusicKit,
  signInToAppleMusic,
  signOutFromAppleMusic,
  isAppleMusicAuthorized,
} from '../utils/musicKitLoader';

// A small Apple-Music control panel for the DJ Console / Settings page.
// Phase 1 of the migration: prove background-audio survival by playing one
// track through MusicKit (same-origin <audio>) instead of the cross-origin
// YouTube iframe. Phase 2 will route the entire playback queue through here.

export const AppleMusicConnectionCard = () => {
  const [music, setMusic] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await getMusicKit();
        if (cancelled) return;
        setMusic(m);
        setAuthorized(isAppleMusicAuthorized());
        // Listen for sign-in/out events fired by the SDK.
        m.addEventListener('authorizationStatusDidChange', () => {
          setAuthorized(isAppleMusicAuthorized());
        });
      } catch (err) {
        setInitError(err?.message || 'init failed');
      } finally {
        if (!cancelled) setInitLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleConnect = async () => {
    setBusy(true);
    try {
      await signInToAppleMusic();
      // Verify a real Music User Token actually landed — if not, Apple's
      // authorize() resolved but storage failed (cookies blocked, popup
      // closed, ITP) and we'd be back in the "isAuthorized lies" state.
      const reallyAuthorized = isAppleMusicAuthorized();
      if (!reallyAuthorized) {
        toast.error('Apple Music sign-in incomplete', {
          description: 'Sign-in popup closed or third-party cookies blocked. Allow cookies for music.apple.com and try again.',
          duration: 10000,
        });
        setAuthorized(false);
        return;
      }
      toast.success('Apple Music connected');
      setAuthorized(true);
    } catch (err) {
      // Surface the actual MusicKit error so we (and the host) can diagnose
      // — the generic "Could not connect" was useless on production. Apple
      // MusicKit error codes that show up here:
      //   - USER_DENIED                — host clicked "Cancel" in popup
      //   - NO_USER_TOKEN              — popup completed but no token persisted (Safari ITP, cookies blocked)
      //   - SUBSCRIPTION_REQUIRED      — Apple ID has no active Apple Music subscription
      //   - AUTHORIZATION_ERROR        — generic auth failure (subscription, cancel, cookies)
      //   - DEVELOPER_TOKEN_INVALID    — backend .p8 signing failed
      const code = err?.errorCode || err?.name || '';
      const rawMsg = err?.message || String(err);
      let label = 'Could not connect to Apple Music';
      let description = rawMsg;
      if (rawMsg.includes('cancel') || code === 'USER_DENIED') {
        label = 'Sign-in cancelled';
        description = '';
      } else if (code === 'SUBSCRIPTION_REQUIRED' || /subscription/i.test(rawMsg)) {
        label = 'Apple Music subscription required';
        description = 'The Apple ID used to sign in must have an active Apple Music plan. Subscribe at music.apple.com then try again.';
      } else if (code === 'NO_USER_TOKEN' || /cookie|popup|token/i.test(rawMsg)) {
        label = 'Sign-in incomplete';
        description = 'The popup closed or third-party cookies are blocked. Allow cookies for music.apple.com and try again.';
      } else if (code === 'AUTHORIZATION_ERROR' || /authoriz/i.test(rawMsg)) {
        label = 'Apple Music sign-in failed';
        description = 'Most common cause: the Apple ID used has no active Apple Music subscription. Sign in to music.apple.com to verify, then retry. If a song plays there, also check that 3rd-party cookies are enabled.';
      } else if (code === 'DEVELOPER_TOKEN_INVALID') {
        label = 'Developer token invalid';
        description = 'Server-side Apple Music key may have expired. Contact admin.';
      }
      console.error('[AppleMusicConnect]', { code, rawMsg, err });
      toast.error(label, { description: description || undefined, duration: 10000 });
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      await signOutFromAppleMusic();
      toast.success('Disconnected from Apple Music');
      setAuthorized(false);
    } catch {
      toast.error('Could not sign out');
    } finally {
      setBusy(false);
    }
  };

  // Plays a single well-known Apple Music track end-to-end so the user
  // can verify background-audio survives screen lock / app switch.
  const handleTestPlay = async () => {
    if (!music) return;
    setTesting(true);
    try {
      // "Tum Hi Ho" by Arijit Singh — known Apple Music catalog ID.
      await music.setQueue({ song: '1073359419' });
      await music.play();
      toast.success('Playing test track. Lock your screen — audio should keep going.', {
        duration: 6000,
      });
    } catch (err) {
      toast.error(err?.message || 'Test play failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl"
      data-testid="apple-music-card"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Apple className="w-5 h-5 text-white" />
          <h3
            className="text-sm font-extrabold text-white uppercase tracking-[0.18em]"
            style={{ fontFamily: 'Outfit' }}
          >
            Apple Music Playback
          </h3>
        </div>
        {!initLoading && (
          authorized ? (
            <span className="text-[9px] font-extrabold uppercase tracking-[0.25em] px-2 py-1 rounded bg-[#39FF14]/15 text-[#39FF14] flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Connected
            </span>
          ) : (
            <span className="text-[9px] font-extrabold uppercase tracking-[0.25em] px-2 py-1 rounded bg-white/5 text-gray-400 flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Not Connected
            </span>
          )
        )}
      </div>
      <p className="text-gray-500 text-[11px] mb-4">
        Sign in with your Apple Music account so playback survives screen-lock + app-switching. Requires an active Apple Music subscription (₹99/mo or your existing family plan).
      </p>

      {initLoading ? (
        <p className="text-gray-500 text-xs flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading MusicKit…
        </p>
      ) : initError ? (
        <p className="text-red-400 text-xs">Couldn&apos;t load MusicKit: {initError}</p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          {!authorized ? (
            <button
              type="button"
              onClick={handleConnect}
              disabled={busy}
              className="bg-white text-black font-bold tracking-wider rounded-full px-5 h-10 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="apple-music-connect-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Apple className="w-4 h-4" />}
              {busy ? 'CONNECTING…' : 'CONNECT APPLE MUSIC'}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleTestPlay}
                disabled={testing}
                className="bg-[#FF007F] hover:bg-[#CC0066] text-white font-bold tracking-wider rounded-full px-5 h-10 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="apple-music-test-play-btn"
              >
                {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Music className="w-4 h-4" />}
                TEST BACKGROUND PLAY
              </button>
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={busy}
                className="border border-white/15 text-white hover:bg-white/5 rounded-full px-4 h-10 text-xs font-bold tracking-wider"
                data-testid="apple-music-disconnect-btn"
              >
                DISCONNECT
              </button>
            </>
          )}
        </div>
      )}

      {authorized && (
        <p className="text-[10px] text-gray-600 mt-3 leading-relaxed font-['JetBrains_Mono']">
          ● Phase 1 — auth + single-track playback online. Phase 2 (full queue + crossfade migration) ships next.
        </p>
      )}
    </div>
  );
};
